import { Directory } from "@capacitor/filesystem"
import { Share } from "@capacitor/share"
import { isNative } from "@utils/underhood/browser"
import { Data, Effect } from "effect"
import { BlobToExtension } from "./mimes"
import { Base64Effect } from "@utils/utilities/effect/data-conversion"
import { SaveFile } from "./filesystem"
import type { FriendlyError } from "@utils/utilities/friendly-error"

export const CanShareNative = Effect.gen(function* () {
    const { value: nativeCanShare } = yield* Effect.tryPromise({
        try: () => Share.canShare(),
        catch: (e) =>
            new CanShareNativeError({
                message: "Failed to check native sharing capability",
                cause: e,
            }),
    })
    return nativeCanShare === true && isNative
}).pipe(Effect.withSpan("CanShareNative"))

export class CanShareNativeError<T>
    extends Data.TaggedError("CanShareNativeError")<{
        message: string
        cause: T
    }>
    implements FriendlyError
{
    get friendly() {
        return "Something went wrong checking if sharing is available!"
    }
}

export const ShareBlob = Effect.fn("ShareBlob")(function* (
    imageBlob: Blob,
    data: {
        url?: string
        title?: string
        text?: string
    } = {},
    filename = "image",
) {
    const nativeCanShare = yield* CanShareNative
    const extension = yield* BlobToExtension(imageBlob)
    if (nativeCanShare === true && isNative) {
        // Native
        Effect.logDebug(`Native sharing mode enabled`)
        const base64 = yield* Base64Effect(imageBlob)
        const { uri } = yield* SaveFileLocal(base64, extension)
        yield* Effect.tryPromise({
            try: () =>
                Share.share({
                    ...data,
                    dialogTitle: "Share Quote",
                    files: [uri],
                }).catch((err) => {
                    if (err.message !== "Share canceled") throw err
                }),
            catch: (cause) =>
                new SharingError({ cause, message: "Failed to share on native platform" }),
        })
    } else {
        // Web
        Effect.logDebug(`Web sharing mode enabled`)
        const file = new File([imageBlob], filename + "." + extension, { type: imageBlob.type })
        const webVersion = { ...data, files: [file] }
        if (!navigator.canShare(webVersion)) {
            return yield* Effect.fail(new SharingNotAllowed({ message: "Sharing is not allowed" }))
        }
        yield* Effect.tryPromise({
            try: () =>
                navigator.share(webVersion).catch((err) => {
                    // Aborting isn't something we should handle
                    if (!(err instanceof DOMException && err.name === "AbortError")) {
                        throw err
                    }
                }),
            catch: (cause) =>
                new SharingError({ cause, message: "Failed to share on web platform" }),
        })
    }
    return yield* Effect.void
})

export class SharingError<T>
    extends Data.TaggedError("SharingError")<{
        message: string
        cause: T
    }>
    implements FriendlyError
{
    get friendly() {
        return "Something went wrong trying to share"
    }
}

export class SharingNotAllowed
    extends Data.TaggedError("SharingNotAllowed")<{
        message: string
    }>
    implements FriendlyError
{
    get friendly() {
        return "Sharing is not allowed on this device"
    }
}

export const SaveFileLocal = Effect.fn("SaveFileLocal")((blob: string, extension: string) =>
    SaveFile(blob, Directory.Cache, `${Date.now()}.${extension}`),
)

// class SharerInstance {
//     constructor(
//         public readonly capacitorShare: boolean,
//         public readonly browserShare: boolean,
//         public readonly canDownload: boolean,
//     ) {}

//     share() {}
// }

// const Sharer = (async () => {
//     // Check native share
//     const capacitorShare = CapacitorShare.canShare().then(({ value }) => value)
//     const browserShare =
//         navigator.canShare &&
//         navigator.canShare({
//             url: "https://acimce.app",
//             text: "Sample text",
//             title: "Sample title",
//         })
//     const canDownload = !isNative && !IS_WEBVIEW
//     return new SharerInstance(await capacitorShare, browserShare, canDownload)
// })()

// export type { SharerInstance }
// export { Sharer }
